import type { NextPage, NextPageContext } from "next";
import getConfig from "next/config";
import Head from "next/head";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import OopsPage from "@pageContent/OopsPage";
import TvSeasonCollection from "@pageContent/TvSeasonCollection";
import VideoHeader from "@pageContent/VideoHeader";
import { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import { Episode } from "@utils/video/dataModels";
import { fetchLatestEpisode, fetchSeasons } from "@utils/video/videoApi";

const {
  publicRuntimeConfig: { cloudinaryDomain },
} = getConfig();

const SeriesPage: NextPage<{ initialEpisode?: Episode | null; initialSeasons?: Array<DropdownOption> | null }> = ({
  initialEpisode,
  initialSeasons,
}) => {
  const { allowedSeries, outletName } = useHostConfig();
  const {
    query: { seriesSlug },
  } = useRouter();

  const latestEpisodeQueryKey = ["latestEpisode", seriesSlug, allowedSeries];

  const {
    isLoading,
    data: episode,
    isError,
    error,
  } = useQuery({
    queryKey: latestEpisodeQueryKey,
    queryFn: () => (seriesSlug ? fetchLatestEpisode(seriesSlug) : null),
    enabled: !initialEpisode,
    initialData: initialEpisode,
  });

  useNoticeError(error, { queryKey: latestEpisodeQueryKey.join(",") });

  if (!episode && !isLoading) return <OopsPage is404={!isError} />;

  const { seriesStructuredData } = episode || {};
  return (
    <>
      {seriesStructuredData && (
        <Head>
          <title>{seriesStructuredData.name}</title>
          <meta property="og:title" content={seriesStructuredData.name} />
          <meta property="og:url" content={seriesStructuredData.url} />
          <meta property="og:description" content={seriesStructuredData.description} />
          <meta property="og:image" content={seriesStructuredData.thumbnailUrl} />
          <meta property="og:type" content="video.tv_show" />
          <meta
            property="og:image"
            content={`${cloudinaryDomain}/image/fetch/w_1200,h_627,c_fill,q_auto,f_auto/${seriesStructuredData.thumbnailUrl}`}
          />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(seriesStructuredData),
            }}
          />
        </Head>
      )}

      <VideoHeader video={episode} />
      <TvSeasonCollection
        key={`${seriesSlug}`}
        activeId={episode?.videoGuid}
        initialSeasons={initialSeasons}
        seriesSlug={seriesSlug as string}
        initialSeasonId={episode?.tvSeasonId}
      />
    </>
  );
};

SeriesPage.getInitialProps = async ({ req, res, query }: NextPageContext) => {
  const { seriesSlug } = query;
  if (!res || !req || !seriesSlug) return {};

  try {
    const [initialEpisode, initialSeasons] = await Promise.all([
      fetchLatestEpisode(seriesSlug),
      fetchSeasons(seriesSlug as string),
    ]);
    if (initialEpisode) return { initialEpisode, initialSeasons };

    res.statusCode = 404;
  } catch (error) {
    console.log(error);
    res.statusCode = 500;
  }
  return {};
};

export default SeriesPage;
